import React, { useState } from 'react';

import { Box, Chip, Popover, SxProps, Theme, Tooltip } from '@mui/material';

import { IChipsProps } from 'src/components/common/types/chipsTypes';

const Chips: React.FC<IChipsProps> = ({
	tags,
	show,
	maxChipLength,
	sx,
	overflowIcon,
	popOverSX,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const chipsToShow = tags.slice(0, show);
	const chipsForPopover = tags.slice(show);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handlePopoverClick = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
	};

	const handleClose: (
		event: React.SyntheticEvent | Event,
		reason?: 'backdropClick' | 'escapeKeyDown',
	) => void = (event) => {
		if ('stopPropagation' in event) {
			event.stopPropagation();
		}
		setAnchorEl(null);
	};

	const truncateText = (text: string, maxLength: number) =>
		text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

	const open = Boolean(anchorEl);

	const renderChip = (chip: (typeof tags)[number], idx: number) => {
		const shouldTruncate =
			maxChipLength && chip.name.length > maxChipLength;
		const chipContent = shouldTruncate
			? truncateText(chip.name, maxChipLength)
			: chip.name;
		const chipElement = (
			<Chip
				key={idx}
				label={chipContent}
				sx={
					{
						cursor: 'pointer',
						...sx,
						...chip.sx,
					} as SxProps<Theme>
				}
			/>
		);

		const tooltipTitle =
			chip.tooltipTitle ?? (shouldTruncate ? chip.name : undefined);

		if (tooltipTitle) {
			return (
				<Tooltip key={idx} title={tooltipTitle}>
					<span>{chipElement}</span>
				</Tooltip>
			);
		}

		return chipElement;
	};

	return (
		<Box display='flex' gap={1}>
			{chipsToShow.map((chip, idx) => renderChip(chip, idx))}

			{chipsForPopover.length > 0 &&
				(overflowIcon ? (
					<Box>{overflowIcon(handleClick)}</Box>
				) : (
					<Chip
						key='counter-chip'
						label={`+${chipsForPopover.length}`}
						onClick={handleClick}
						sx={{
							cursor: 'pointer',
							...sx,
						}}
					/>
				))}

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				slotProps={{
					paper: {
						sx: {
							mt: 1,
							p: 2,
							borderRadius: '10px',
							maxWidth: '300px',
						},
						onClick: handlePopoverClick,
					},
				}}
				sx={{ ...popOverSX }}
			>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
					{chipsForPopover.map((tag, index) =>
						renderChip(tag, show + index),
					)}
				</Box>
			</Popover>
		</Box>
	);
};

export default Chips;
